import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import { FaArrowRight, FaCheckCircle, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { IoChevronBack } from "react-icons/io5";
import Header from "./Header";
import {
  RESET_LOGIN_CODE_SUCCESS_MESSAGE,
  resetAuthenticatedLoginCode,
} from "../../common/api/resetPasswordApi";
import { PROFILE_ROLE_CAREGIVER } from "../../common/api/profileApi";
import { getStoredUserEmail } from "../../common/utils/sessionContext";
import "../../styles/familymember.css";
import "../../styles/caregiver/profile.css";

const sanitizeLoginCodeInput = (value) => String(value || "").replace(/\D/g, "").slice(0, 4);

const LoginCodeField = ({
  id,
  label,
  value,
  showValue,
  onToggleVisibility,
  onChange,
  error,
}) => (
  <div className="family-profile-field">
    <label htmlFor={id} className="family-profile-field-label">
      {label}
    </label>
    <div className="family-reset-password-input-wrap">
      <FaLock className="family-reset-password-input-icon" aria-hidden="true" />
      <input
        id={id}
        type={showValue ? "text" : "password"}
        inputMode="numeric"
        maxLength={4}
        autoComplete="one-time-code"
        className="family-profile-field-input family-reset-password-input caregiver-login-code-input"
        value={value}
        onChange={(e) => onChange(sanitizeLoginCodeInput(e.target.value))}
        required
      />
      <button
        type="button"
        className="family-reset-password-toggle"
        onClick={onToggleVisibility}
        aria-label={showValue ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}>
        {showValue ? <FaEye /> : <FaEyeSlash />}
      </button>
    </div>
    {error ? (
      <p className="family-reset-password-error family-reset-password-error--field" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const CaregiverResetPassword = () => {
  const navigate = useNavigate();
  const [currentLoginCode, setCurrentLoginCode] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [confirmLoginCode, setConfirmLoginCode] = useState("");
  const [showCurrentLoginCode, setShowCurrentLoginCode] = useState(false);
  const [showLoginCode, setShowLoginCode] = useState(false);
  const [showConfirmLoginCode, setShowConfirmLoginCode] = useState(false);
  const [currentLoginCodeError, setCurrentLoginCodeError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const codesMatch = loginCode.length === 4 && loginCode === confirmLoginCode;
  const canSubmit =
    currentLoginCode.length === 4 &&
    loginCode.length === 4 &&
    confirmLoginCode.length === 4 &&
    codesMatch &&
    !isSubmitting &&
    !isSuccess;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCurrentLoginCodeError("");
    setFormError("");

    if (!canSubmit) return;

    const emailId = getStoredUserEmail();
    if (!emailId) {
      setFormError("Unable to reset login code. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    const result = await resetAuthenticatedLoginCode({
      emailId,
      currentLoginCode,
      loginCode,
      confirmLoginCode,
      role: PROFILE_ROLE_CAREGIVER,
    });
    setIsSubmitting(false);

    if (!result.success) {
      if (result.field === "currentLoginCode") {
        setCurrentLoginCodeError(result.message);
      } else {
        setFormError(result.message);
      }
      return;
    }

    setIsSuccess(true);
  };

  const goToProfile = () => {
    navigate("/caregiver-profile", { replace: true });
  };

  return (
    <div className="caregiver-page-layout caregiver-settings-layout">
      <Header noBorderBottom />
      <Backdrop
        sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={isSubmitting}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className="caregiver-settings-page caregiver-profile-page caregiver-reset-login-code-page">
        <div className="family-checklist-topbar">
          <button
            type="button"
            className="family-checklist-back"
            onClick={goToProfile}
            aria-label="Back to profile"
            disabled={isSuccess}>
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Reset login code</h1>
          <span className="family-vitals-topbar-spacer" aria-hidden="true" />
        </div>

        {isSuccess ? (
          <div className="family-reset-password-success-wrap">
            <div
              className="bt-forgot-success-card family-reset-password-success-card"
              role="status"
              aria-live="polite">
              <FaCheckCircle className="bt-forgot-success-card-icon" aria-hidden="true" />
              <h2 className="bt-forgot-success-card-title">{RESET_LOGIN_CODE_SUCCESS_MESSAGE}</h2>
              <button type="button" className="bt-login-submit bt-forgot-success-card-btn" onClick={goToProfile}>
                <IoChevronBack aria-hidden="true" />
                <span>back to profile</span>
              </button>
            </div>
          </div>
        ) : (
          <form className="caregiver-reset-login-code-form" onSubmit={handleSubmit}>
            <p className="caregiver-reset-login-code-intro">
              Enter your current login code and choose a new 4-digit login code.
            </p>

            <LoginCodeField
              id="cg-reset-current-login-code"
              label="Current login code"
              value={currentLoginCode}
              showValue={showCurrentLoginCode}
              onToggleVisibility={() => setShowCurrentLoginCode((open) => !open)}
              onChange={(value) => {
                setCurrentLoginCode(value);
                setCurrentLoginCodeError("");
                setFormError("");
              }}
              error={currentLoginCodeError}
            />

            <LoginCodeField
              id="cg-reset-new-login-code"
              label="New login code"
              value={loginCode}
              showValue={showLoginCode}
              onToggleVisibility={() => setShowLoginCode((open) => !open)}
              onChange={(value) => {
                setLoginCode(value);
                setFormError("");
              }}
            />

            <LoginCodeField
              id="cg-reset-confirm-login-code"
              label="Confirm new login code"
              value={confirmLoginCode}
              showValue={showConfirmLoginCode}
              onToggleVisibility={() => setShowConfirmLoginCode((open) => !open)}
              onChange={(value) => {
                setConfirmLoginCode(value);
                setFormError("");
              }}
            />

            {formError ? (
              <p className="family-reset-password-error" role="alert">
                {formError}
              </p>
            ) : null}

            <button
              type="submit"
              className="bt-login-submit caregiver-reset-login-code-submit"
              disabled={!canSubmit}>
              <span>{isSubmitting ? "Resetting…" : "Reset login code"}</span>
              <FaArrowRight aria-hidden="true" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CaregiverResetPassword;
